import { useMemo, useState } from 'react'
import { useAccount } from 'wagmi'
import { parseUnits, isAddress } from 'viem'
import { T } from '@/lib/theme'
import { Button, Input, MaxLink, Panel, SectionLabel, Stat, ToggleButton, Badge } from '@/components/ui'
import { ADDRESSES, IS_LIVE } from '@/lib/contracts'
import veAbi from '@/lib/abis/HorizonVoteEscrow.json'
import { fmtBalance, toNumber, validateAmount } from '@/lib/utils'
import { useAction } from '@/hooks/useAction'
import { useVeNFTs } from '@/hooks/useVeNFTs'

const ZERO = '0x0000000000000000000000000000000000000000'
const VE_NOT_DEPLOYED = !IS_LIVE || (ADDRESSES.HORIZON_VOTE_ESCROW || '').toLowerCase() === ZERO
const WEEK = 7 * 86_400
const MAX_LOCK = 4 * 365 * 86_400
const MIN_LOCK_DAYS = WEEK / 86_400      // 7 - the contract minimum and its week-rounding unit
const MAX_LOCK_DAYS = MAX_LOCK / 86_400  // 1460 (4 years) - the ceiling the contract enforces

/// Lock HORIZON for the ve(3,3) power triad: BOOST on your own farm emissions, a VOTE on where
/// emissions and fees flow (GaugeController), and a share of protocol REVENUE. Straight off
/// HorizonVoteEscrow, which is now a transferable veNFT: each lock is its own token, so a wallet holds
/// many, and each carries its own amount, unlock date and power. Two flavors: a decaying time-lock
/// (power = amount x remaining / 4y, glides to zero at the unlock date, HORIZON returns via withdraw)
/// and a permanent BURN-lock (HORIZON destroyed forever, a flat 2x power that never decays).
///
/// Layout follows the ve(3,3) reference apps (Aerodrome / Velodrome): voting power is the hero number,
/// each position is a card with its own manage actions, and creation is amount + a duration slider
/// with a live power preview. Burn-lock is Horizon's permanent option in place of their max-lock.
export function LockPanel({ d, onTx }) {
  const { address: user } = useAccount()
  const action = useAction()
  const ve = ADDRESSES.HORIZON_VOTE_ESCROW

  const { positions, userPower, totalPower, refetch } = useVeNFTs(user)

  // ── Create form ──────────────────────────────────────────────────────────
  const [amount, setAmount] = useState('')
  const [durationDays, setDurationDays] = useState('') // days field, empty until entered (like the amount field)
  const [mode, setMode] = useState('lock') // 'lock' | 'burn'
  const [burnArmed, setBurnArmed] = useState(false)
  const isBurn = mode === 'burn'

  const err = amount ? validateAmount(amount) : ''
  const wei = useMemo(() => { try { return amount && !err ? parseUnits(amount, 18) : 0n } catch { return 0n } }, [amount, err])
  const nowSec = Math.floor(Date.now() / 1000)
  const daysNum = Math.floor(Number(durationDays)) || 0
  const durationValid = daysNum >= MIN_LOCK_DAYS && daysNum <= MAX_LOCK_DAYS
  const durationSecs = Math.min(Math.max(daysNum * 86_400, WEEK), MAX_LOCK)
  const targetEnd = Math.floor((nowSec + durationSecs) / WEEK) * WEEK

  // Power this create grants: a decaying lock is amount x remaining/4y; a burn is a flat 2x.
  const grantedPowerWei = isBurn ? (wei * 2n) : (wei * BigInt(Math.max(targetEnd - nowSec, 0))) / BigInt(MAX_LOCK)
  const newUserPower = userPower + grantedPowerWei
  const newTotalPower = totalPower + grantedPowerWei
  const curSharePct = totalPower > 0n ? (toNumber(userPower) / toNumber(totalPower)) * 100 : 0
  const newSharePct = newTotalPower > 0n ? (toNumber(newUserPower) / toNumber(newTotalPower)) * 100 : 0
  const previewActive = wei > 0n && (isBurn || durationValid)

  const overBalance = !!user && wei > 0n && wei > (d?.userHorizonBalWei ?? 0n)
  const canSubmit = !!user && wei > 0n && !overBalance && (isBurn || durationValid)
  const horizonPull = { token: ADDRESSES.HORIZON_TOKEN, spender: ve, amount: wei }

  async function submitCreate() {
    let res
    if (isBurn) {
      if (!burnArmed) { setBurnArmed(true); return }
      setBurnArmed(false)
      res = await action.run({
        pulls: [horizonPull],
        calls: [{ address: ve, abi: veAbi, functionName: 'burnLock', args: [wei] }],
        permit: { token: ADDRESSES.HORIZON_TOKEN, amount: wei, spender: ve,
          buildCall: (p, sig) => ({ address: ve, abi: veAbi, functionName: 'burnLockWithPermit', args: [p, sig] }) },
        successMsg: `Burned ${amount} HORIZON for a permanent lock.`,
      })
    } else {
      const unlock = BigInt(nowSec + durationSecs)
      res = await action.run({
        pulls: [horizonPull],
        calls: [{ address: ve, abi: veAbi, functionName: 'createLock', args: [wei, unlock] }],
        permit: { token: ADDRESSES.HORIZON_TOKEN, amount: wei, spender: ve,
          buildCall: (p, sig) => ({ address: ve, abi: veAbi, functionName: 'createLockWithPermit', args: [unlock, p, sig] }) },
        successMsg: `Locked ${amount} HORIZON until ${new Date(targetEnd * 1000).toLocaleDateString()}.`,
      })
    }
    if (!res) return
    setAmount('')
    await refetch()
    onTx?.()
  }

  if (VE_NOT_DEPLOYED) return null

  return (
    <Panel style={{ padding: 32 }}>
      <SectionLabel>Lock HORIZON</SectionLabel>
      <p style={{ color: T.textDim, fontSize: 14, lineHeight: 1.6, margin: '10px 0 20px' }}>
        Commit HORIZON for voting power: direct where emissions and fees flow, and earn a share of
        protocol revenue. Lock for up to four years, or burn for a permanent 2x that never decays.
      </p>

      {/* Hero: the wallet's aggregate power, the ve(3,3) headline number */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 24 }}>
        <Stat label="Your voting power" value={fmtBalance(toNumber(userPower), 2)} color={T.positive} size="lg" />
        <Stat label="Share of all power" value={`${curSharePct.toFixed(2)}%`} />
        <Stat label="Positions" value={String(positions.length)} />
      </div>

      {/* Positions - one card per veNFT (Aerodrome/Velodrome pattern) */}
      {positions.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ color: T.textDim, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
            Your positions
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {positions.map((p) => (
              <PositionCard key={String(p.id)} pos={p} user={user} action={action} ve={ve} onDone={async () => { await refetch(); onTx?.() }} sTokenBal={d?.userHorizonBalWei ?? 0n} />
            ))}
          </div>
        </div>
      )}

      {/* Create */}
      <div style={{ color: T.textDim, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
        {positions.length > 0 ? 'New position' : 'Create a lock'}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <ToggleButton active={!isBurn} onClick={() => { setMode('lock'); setBurnArmed(false) }}>Time-lock</ToggleButton>
        <ToggleButton active={isBurn} onClick={() => { setMode('burn'); setBurnArmed(false) }}>Burn for permanent power</ToggleButton>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <Input placeholder="Amount of HORIZON" value={amount} onChange={(v) => { setAmount(v); setBurnArmed(false) }} />
        </div>
        <MaxLink onClick={() => setAmount(String(toNumber(d?.userHorizonBalWei ?? 0n)))} />
      </div>

      {/* Duration: amount-in-days + MAX (time-lock only), mirroring the HORIZON amount field.
          MAX = the 4-year ceiling the contract enforces. */}
      {!isBurn && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <span style={{ color: T.textDim, fontSize: 13 }}>Lock length</span>
            <span style={{ color: durationValid ? T.text : T.textDim, fontSize: 13 }}>
              {durationValid ? `unlocks ${new Date(targetEnd * 1000).toLocaleDateString()}` : `${MIN_LOCK_DAYS}–${MAX_LOCK_DAYS} days`}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <Input type="number" suffix="days" placeholder="Lock length in days" value={durationDays} onChange={setDurationDays} />
            </div>
            <MaxLink onClick={() => setDurationDays(String(MAX_LOCK_DAYS))} />
          </div>
        </div>
      )}

      {previewActive && (
        <div style={{ color: T.textDim, fontSize: 13, marginBottom: 14, lineHeight: 1.7 }}>
          <div>
            Grants <b style={{ color: T.positive }}>{fmtBalance(toNumber(grantedPowerWei), 2)}</b> power
            {isBurn ? ', permanent. Burning destroys the HORIZON forever.' : `, gliding to 0 by ${new Date(targetEnd * 1000).toLocaleDateString()}.`}
          </div>
          <div>Share of all power: {curSharePct.toFixed(2)}% → <b style={{ color: T.positive }}>{newSharePct.toFixed(2)}%</b></div>
        </div>
      )}

      {overBalance && <div style={{ color: T.negative, fontSize: 13, marginBottom: 10 }}>Amount exceeds your HORIZON balance.</div>}

      <Button
        onClick={submitCreate} disabled={!canSubmit || action.pending}
        style={isBurn && burnArmed ? { background: T.negative } : undefined}
      >
        {isBurn ? (burnArmed ? 'Press again to burn permanently' : `Burn ${amount ? amount + ' ' : ''}HORIZON`) : `Lock ${amount ? amount + ' ' : ''}HORIZON`}
      </Button>

    </Panel>
  )
}

/// One veNFT position, with its manage actions inline. Decaying locks can add, extend, transfer, and
/// (once expired) withdraw; a burn-lock is permanent, so it only transfers.
function PositionCard({ pos, user, action, ve, onDone, sTokenBal }) {
  const [manage, setManage] = useState(null) // 'add' | 'extend' | 'transfer' | null
  const [val, setVal] = useState('')
  const nowSec = Math.floor(Date.now() / 1000)
  const expired = !pos.isBurn && pos.end <= nowSec && pos.amount > 0n
  const remaining = pos.isBurn ? 1 : Math.max(pos.end - nowSec, 0) / MAX_LOCK // 0..1 for the bar

  const status = pos.isBurn
    ? <Badge color={T.negative}>Permanent</Badge>
    : expired
      ? <Badge color={T.textDim}>Unlocked</Badge>
      : <Badge color={T.accent}>Unlocks {new Date(pos.end * 1000).toLocaleDateString()}</Badge>

  async function addAmount() {
    let w; try { w = parseUnits(val || '0', 18) } catch { return }
    if (w <= 0n) return
    const res = await action.run({
      pulls: [{ token: ADDRESSES.HORIZON_TOKEN, spender: ve, amount: w }],
      calls: [{ address: ve, abi: veAbi, functionName: 'increaseAmount', args: [pos.id, w] }],
      successMsg: 'Added to your lock.',
    })
    if (res) { setManage(null); setVal(''); onDone?.() }
  }
  async function extend() {
    const days = Math.floor(Number(val)) || 0
    if (days < MIN_LOCK_DAYS) return
    const unlock = BigInt(Math.floor((nowSec + Math.min(days * 86_400, MAX_LOCK)) / WEEK) * WEEK)
    const res = await action.run({
      calls: [{ address: ve, abi: veAbi, functionName: 'increaseUnlockTime', args: [pos.id, unlock] }],
      successMsg: 'Lock extended.',
    })
    if (res) { setManage(null); setVal(''); onDone?.() }
  }
  async function transfer() {
    if (!isAddress(val)) return
    const res = await action.run({
      calls: [{ address: ve, abi: veAbi, functionName: 'transferFrom', args: [user, val, pos.id] }],
      successMsg: 'Position transferred.',
    })
    if (res) { setManage(null); setVal(''); onDone?.() }
  }
  async function withdraw() {
    const res = await action.run({ calls: [{ address: ve, abi: veAbi, functionName: 'withdraw', args: [pos.id] }], successMsg: 'Lock withdrawn.' })
    if (res) onDone?.()
  }

  return (
    <Panel elevated style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ color: T.text, fontSize: 18, fontWeight: 600 }}>{fmtBalance(toNumber(pos.amount), 2)}</span>
          <span style={{ color: T.textDim, fontSize: 13 }}>HORIZON</span>
          {status}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: T.positive, fontSize: 16, fontWeight: 600 }}>{fmtBalance(toNumber(pos.power), 2)}</div>
          <div style={{ color: T.textDim, fontSize: 11 }}>voting power</div>
        </div>
      </div>

      {!pos.isBurn && (
        <div style={{ height: 4, background: T.border, borderRadius: 2, margin: '12px 0 0', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${Math.round(remaining * 100)}%`, background: T.accent }} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        {expired && <Button variant="primary" onClick={withdraw} disabled={action.pending}>Withdraw</Button>}
        {!pos.isBurn && !expired && <Button variant="ghost" onClick={() => { setManage(manage === 'add' ? null : 'add'); setVal('') }}>Add</Button>}
        {!pos.isBurn && !expired && <Button variant="ghost" onClick={() => { setManage(manage === 'extend' ? null : 'extend'); setVal('') }}>Extend</Button>}
        <Button variant="ghost" onClick={() => { setManage(manage === 'transfer' ? null : 'transfer'); setVal('') }}>Transfer</Button>
      </div>

      {manage && (
        <div style={{ marginTop: 12 }}>
          {manage === 'transfer' && (
            <p style={{ color: T.textDim, fontSize: 12, lineHeight: 1.6, margin: '0 0 8px' }}>
              Moving a position resets this epoch's gauge votes for both wallets. The new owner re-votes at their own power.
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <Input
                value={val} onChange={setVal}
                placeholder={manage === 'add' ? 'Amount of HORIZON' : manage === 'extend' ? `Extend to (days, ${MIN_LOCK_DAYS}-${MAX_LOCK_DAYS})` : 'Recipient address'}
              />
            </div>
            {manage === 'add' && <MaxLink onClick={() => setVal(String(toNumber(sTokenBal)))} />}
            <Button
              onClick={manage === 'add' ? addAmount : manage === 'extend' ? extend : transfer}
              disabled={action.pending || !val}
            >
              {manage === 'add' ? 'Add' : manage === 'extend' ? 'Extend' : 'Send'}
            </Button>
          </div>
        </div>
      )}
    </Panel>
  )
}

